import React, { FC, memo, ReactNode, useMemo } from 'react';
import { CircularProgress, useTheme } from '@mui/material';
import orderBy from 'lodash/orderBy';
import moment from 'moment-timezone';
import { nanoid } from 'nanoid';

import { IDepartmentScheduleDTO } from 'src/api/dtos';
import { ComponentWithFade } from 'src/shared/hocs';
import { DateRangeType } from 'src/shared/types';
import { isDateWithinDateRange } from 'src/shared/utils';

import { MonthlyCell } from './MonthlyCell';
import './MonthlyCalendar.styles.scss';

type VariantType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'muted' | 'danger' | 'pending';

export type MonthlyEventType = {
  type: VariantType;
  range: DateRangeType;
  title: ReactNode;
  description?: string;
  containerClass?: string;
  popupOrder?: number;
  unpublished?: boolean;
  tooltip?: ReactNode;
  onClick?: () => void;
};

const NUM_DAYS_IN_WEEK = 7;

interface Props {
  month: moment.Moment;
  events?: MonthlyEventType[];
  departmentSchedules?: IDepartmentScheduleDTO[];
  loading?: boolean;
  className?: string;
  onSelectDate?: (date: string) => void;
}

export const MonthlyCalendar: FC<Props> = memo(
  ({ month, events = [], departmentSchedules = [], loading, className, onSelectDate }) => {
    const theme = useTheme();

    const cells = useMemo(() => {
      const sortedEvents = orderBy(events, [(event) => !event.popupOrder, 'popupOrder'], ['desc', 'asc']);

      const monthStart = month.clone().startOf('month');
      const monthEnd = monthStart.clone().endOf('month');
      const startDate = monthStart.clone().startOf('week');
      const endDate = monthEnd.clone().endOf('week');

      let day = startDate.clone();
      const rows: ReactNode[] = [];
      let week: ReactNode[] = [];
      let showDays = true;

      while (day <= endDate) {
        for (let i = 0; i < NUM_DAYS_IN_WEEK; i++) {
          const disabled = !day.isSame(monthStart, 'month');
          const dailyEvents = sortedEvents.filter((e) => isDateWithinDateRange(day, e.range, 'day'));
          const isLastCellInGrid = day.clone().add(1, 'day') > endDate;
          const departmentSchedulesForDay =
            departmentSchedules.filter((schedule) => moment(schedule.date).isSame(day, 'day')) || null;

          week = [
            ...week,
            <MonthlyCell
              key={nanoid()}
              day={day.clone()}
              events={dailyEvents}
              disabled={disabled}
              showDays={showDays}
              isLastCellInGrid={isLastCellInGrid}
              departmentSchedule={departmentSchedulesForDay}
              onSelectDate={onSelectDate}
            />,
          ];
          day = day.add(1, 'day');
        }

        rows.push(
          <div className="weekly-row" key={`${day.format()}`}>
            {week}
          </div>,
        );
        week = [];

        if (showDays) {
          showDays = false;
        }
      }

      return <div className="monthly-calendar-rows">{rows}</div>;
    }, [month, events]);

    return (
      <div className={`monthly-calendar ${className} ${theme.palette.mode}`}>
        {cells}
        <ComponentWithFade
          show={!!loading}
          transitionStyles={{ entered: { opacity: 0.6 } }}
          className="monthly-calendar-loader"
        >
          <CircularProgress />
        </ComponentWithFade>
      </div>
    );
  },
);
